import React from 'react';
import { TabIcons } from '../BottomTabs';
import { TabItem } from '../BottomTabs';

/**
 * Utility functions for tab configuration and content styling
 */

/**\n * Default tabs configuration for DinnerBrain app\n * Optimized for mobile navigation with 6 essential tabs including Add Recipe\n * Order: Recipes → Meal Plan → Add → Shopping → Community → Profile\n */
export const getDefaultTabs = (
  tabBadges: Record<string, number | string> = {},
  includeActionButton: boolean = false
): TabItem[] => {
  const tabs: TabItem[] = [
    {
      id: 'home',
      label: 'Recipes',
      icon: TabIcons.home,
      activeIcon: TabIcons.homeActive,
      route: '/app/recipes',
      badge: tabBadges.home
    },
    {
      id: 'meal-plan',
      label: 'Meal Plan',
      icon: TabIcons.mealPlan,
      activeIcon: TabIcons.mealPlanActive,
      route: '/app/meal-plan',
      badge: tabBadges.mealPlan
    }
  ];

  // Conditionally add the action button
  if (includeActionButton) {
    tabs.push({
      id: 'add-recipe',
      label: '',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
        </svg>
      ),
      activeIcon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
        </svg>
      ),
      route: '/app/recipes/add',
      isAction: true
    });
  }

  tabs.push(
    {
      id: 'shopping-list',
      label: 'Shopping',
      icon: TabIcons.shoppingList,
      activeIcon: TabIcons.shoppingListActive,
      route: '/app/shopping-list',
      badge: tabBadges.shoppingList
    },
    {
      id: 'community',
      label: 'Community',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
      ),
      activeIcon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
      ),
      route: '/app/community',
      badge: tabBadges.community
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: TabIcons.profile,
      activeIcon: TabIcons.profileActive,
      route: '/app/profile',
      badge: tabBadges.profile
    }
  );

  return tabs;
};

/**
 * Calculate content style for the main content area
 */
export const getContentStyle = (
  isKeyboardVisible: boolean,
  safeAreaBottom: number,
  safeAreaTop: number = 0,
  tabBarHeight: number = 83
): React.CSSProperties => ({
  paddingTop: safeAreaTop > 0 ? `${safeAreaTop}px` : undefined,
  paddingBottom: isKeyboardVisible ? 0 : `${tabBarHeight + safeAreaBottom}px`,
  minHeight: '100vh',
  transition: 'padding-bottom 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
});